import { DeviceScale, IFrameData, IScreenPacket, ScreenMessageFlags } from "app/modules/remote-control-ng/definitions";
import { RcLoggerService } from "./rcLoggerService";
import { IDeviceContextHandler, IDeviceContext, IDeviceScreenTransformService, ScaleResult, IBaseDeviceContext } from "../mcrc-api";
import { DeviceContext, IRcCanvasElement } from "../context/deviceContext";
import { PerformanceService } from "./performanceService";
import { DeviceScreenRenderingService } from "./deviceScreenRenderingService";
import { MathHelpers } from "../utils/math";
import { RcContext } from "../context/rcContext";
import { RcTransformationService } from "./rcTransformation.service";
import { OsDetectionService } from "./osDetectionService";
import { RestartThrottling } from "./restartThrottling";
import { VideoRotationMessage } from "../messages/xsight";
import { ThAckMessage } from "../messages/pocket-com";
import { Injector } from "@angular/core";
import { ArrayHelper } from "../utils/arrayHelper";

export class DeviceScreenTransformService implements IDeviceScreenTransformService {
  // Default number of animation frames to execute.
  // Under ideal conditions, total animation duration should end up being approximately:
  // VSYNC * MAX_TRANSFORM_FRAMES = (1000/60 * 15) = 250 milliseconds
  // currently use one frame to complete all changes
  private MAX_TRANSFORM_FRAMES: number = 1;

  // Max transform animation duration in milliseconds (after which time the animation is set to end transform)
  private ANIMATION_MAX_DURATION: number = 350;

  /**
   * TODO RC Refactor 2024.0.4 - test and benchmarking on different devices
   * In order to get better performance and smooth displaying, there is a tradeoff between the frequency of updateScreen
   * and the time of each image.onload callback. Based on the test, the major factor of the stuck is the length of data.frameData.
   *
   * Currently will start batch updating the screen when number of frames exceeds 80 in a single screen message.
   * e.g. if we have 90 frames in one screen message, the batch size will be 90 x 0.3 = 30, which means
   * the screen will be updated after processing 30 frames. Such a screen message will invoke updateScreen
   * 4 times (90, 60, 30, 0)
   *
   * FRAME_BATCH_FACTOR can be set to 0 to disable batch updates
   */
  private FRAME_BATCH_FACTOR: number = 0;
  private FRAME_BATCH_THRESHOLD: number = 80;
  private FRAME_BATCH_MIN_THRESHOLD = 50;
  private FRAME_BATCH_MAX_THRESHOLD = 150;

  // zoom/rotate related
  private autoZoomScale: number;
  private autoZoomActive: boolean;
  private customZoomScale: number;
  private targetScale: DeviceScale;
  private transformRunFinished: boolean = true;
  private currentTargetRotation = DeviceScale.ScaleUnknown;
  private currentTargetScale = DeviceScale.ScaleUnknown;
  private currentClientScale = DeviceScale.ScaleUnknown;
  // only been called by zoom (handleZoom in rc-zoom.components.ts)
  // NOTE: will not have animation inside the code
  private currentAnimations: any;
  private endOfZoomCallbacks: Function[] = [];

  private shuttingDown: boolean = false;

  private zoomRotateService: RcTransformationService;
  private isInTransition: boolean;
  private logger: RcLoggerService;
  private perf: PerformanceService;

  private _canvasOuter: HTMLElement;

  // ctx should be IRcDeviceContext or just DeviceContext?
  constructor(private ctx: DeviceContext, private deviceContextHandler: IDeviceContextHandler) {
    this.targetScale = DeviceScale.ScaleAuto;
    this.autoZoomActive = true;
    this.zoomRotateService = RcContext.instance.getService(RcTransformationService);
    this.logger = RcContext.instance.getService(RcLoggerService);
    this.perf = RcContext.instance.getService(PerformanceService);
  }

  private get canvasOuter() {
    if (!this._canvasOuter) {
      this._canvasOuter = document.getElementById("canvasOuter");
    }
    return this._canvasOuter;
  }

  getAutoZoomActive(): boolean {
    return this.autoZoomActive;
  }

  getAutoZoomScale(): number {
    return this.autoZoomScale;
  }

  getCustomZoomScale(): number {
    return this.customZoomScale;
  }

  getTHTargetScale(): DeviceScale {
    return this.targetScale;
  }

  getCustomScale(): number {
    return this.customZoomScale;
  }

  getScreenScale(): number {
    let screenScale;
    const context = this.deviceContextHandler.currentContext();
    if (context.skin.loaded) {
      screenScale = (this.autoZoomActive ? this.autoZoomScale : this.customZoomScale) * this.getScreenToSkinRatio(context);
    } else {
      screenScale = this.customZoomScale;
    }
    return screenScale;
  }

  /**
   * Returns the ratio between the Skin's screen area and the device's screen
   * */
  getScreenToSkinRatio(context?: IDeviceContext): number {
    var ratio;
    if (!context) {
      context = this.deviceContextHandler.currentContext();
    }

    if (context.skin.width > 0) {
      ratio = context.skin.screen.width / context.device.width;
    } else {
      ratio = 1;
    }

    return ratio;
  }

  setTargetScale(scale: DeviceScale): void {
    this.targetScale = scale;

    if (this.targetScale == DeviceScale.ScaleAuto) {
      this.autoZoomActive = true;
    } else if (this.targetScale == DeviceScale.ScaleCustom) {
      this.autoZoomActive = false;
    }
  }

  setCustomScale(scale: number): void {
    if (!this.isCanvasVisible()) return;

    this.customZoomScale = scale;
    var ret = this.checkIfAutoZoomAndGetScale();

    if (this.shouldRotationAndScaleUpdate(this.ctx.targetRotation, ret.scale)) {
      this.updateScaleAndRotation(this.ctx.rotation, ret.scale, ret.clientScale, ret.clientSideRotation);
    }
  }

  isAutoZoomActive(): boolean {
    return this.autoZoomActive;
  }

  checkIfAutoZoomAndGetScale(): ScaleResult {
    if (!this.isCanvasVisible()) {
      let result = {
        scale: this.ctx.scale,
        clientScale: this.ctx.clientScaleFactor,
        clientSideRotation: this.ctx.targetClientSideRotation
      };
      this.logger.trace("checkIfAutoZoomAndGetScale: " + JSON.stringify(result));
      return result;
    }

    var receivedScale = this.ctx.saveScale;
    var scaleToReturn = this.ctx.saveScale;
    var clientScale = 1;

    var targetScaleIsAuto = (this.targetScale as DeviceScale == DeviceScale.ScaleAuto);
    var targetScaleIsCustom = (this.targetScale == DeviceScale.ScaleCustom);

    if (this.autoZoomActive && !targetScaleIsAuto && !targetScaleIsCustom) {
      if (receivedScale == this.targetScale) {
        this.autoZoomActive = false;
      }
      else {
        this.calculateAutoZoomParameters(receivedScale);
        scaleToReturn = this.autoZoomScale;
      }
    }
    else if (this.autoZoomActive && targetScaleIsAuto) {
      clientScale = this.calculateAutoZoomParameters(receivedScale);
      scaleToReturn = this.autoZoomScale;
    }
    else if (targetScaleIsCustom) {
      clientScale = this.calculateCustomZoomParameters(receivedScale);
      scaleToReturn = this.customZoomScale;
    }

    let result = {
      scale: scaleToReturn,
      clientScale: clientScale,
      clientSideRotation: this.ctx.targetClientSideRotation
    };
    this.logger.trace("checkIfAutoZoomAndGetScale: " + JSON.stringify(result));
    return result;
  }

  /**
   * After transferring we have to rescale the image, to fit the new canvas size.
   * We don't need to wait for a full screen to arrive as we transferred the canvas
   */
  updateAfterTransfer(): void {
    this._canvasOuter = null;
    const context = this.deviceContextHandler.currentContext();

    // Calculate the new auto-zoom scale if we are in autoZoom
    const ret = this.checkIfAutoZoomAndGetScale();
    if (this.shouldRotationAndScaleUpdate(this.ctx.targetRotation, ret.scale, ret.clientScale)) {
      this.logger.debug('updateAfterTransfer: ' + JSON.stringify({ rotation: context.device.rotation, targetRotation: context.client.targetRotation, scale: ret.scale, clientScaleFactor: ret.clientScale, clientRotation: context.client.rotation }));
      const scale = this.autoZoomActive ? ret.scale : context.device.scale + 1;

      // after the transfer, the screen might still not fit the skin, as the real check for
      // aligning the skin and screen orientations is done by comparing their height/width ratios.
      // This can be done accurately only on full screen messages, thus the restart message
      RcContext.instance.getService(RestartThrottling).send(scale, true, RcContext.instance)
    }
  }

  updateSkin = (data: any): void => {
    if (data) {
      this.ctx.deviceHeight = data.image.height;
      this.ctx.deviceWidth = data.image.width;
      this.ctx.screenOffsetX = data.offsetX;
      this.ctx.screenOffsetY = data.offsetY;
      const context = this.deviceContextHandler.currentContext();
      this.ctx.imageSkin = context.skin.isIrregular ? data.image : DeviceScreenRenderingService.convertColorToAlpha(data.image);
    } else {
      // Create a dummy skin to simplify the logic flow, so that it always follows
      // the same path for both skin and non-skin devices
      this.ctx.imageSkin = {
        width: this.ctx.deviceWidth,
        height: this.ctx.deviceHeight,
        emptySkin: true
      } as IRcCanvasElement;
      this.ctx.skinScreenWidth = this.ctx.deviceWidth;
      this.ctx.skinScreenHeight = this.ctx.deviceHeight;
    }

    this.ctx.skinDone = true;
  }

  updateTransform(): void {
    var ret = this.checkIfAutoZoomAndGetScale();
    if (this.shouldRotationAndScaleUpdate(this.ctx.rotation, ret.scale, ret.clientScale)) {
      this.updateScaleAndRotation(this.ctx.rotation, ret.scale, ret.clientScale, ret.clientSideRotation);
    }
  }

  updateTransformAndScreen(screenPacket: IScreenPacket): void {
    this.updateCapture(screenPacket);

    if (screenPacket.fullscreen) {
      var ret = this.checkIfAutoZoomAndGetScale();
      if (this.shouldRotationAndScaleUpdate(screenPacket.rotation, ret.scale, ret.clientScale)) {
        this.updateScaleAndRotation(screenPacket.rotation, ret.scale, ret.clientScale, ret.clientSideRotation, undefined, screenPacket.imageDataFlags);
      }
    }

    this.updateScreen(screenPacket.fullscreen);
  }

  subscribeToEndOfTransformations(callback: Function): void {
    let index = this.endOfZoomCallbacks.indexOf(callback);
    if (index == -1) {
      this.endOfZoomCallbacks.push(callback);
    }
  }

  unsubscribeToEndOfTransformations(callback: Function): void {
    let index = this.endOfZoomCallbacks.indexOf(callback);
    if (index > -1) {
      this.endOfZoomCallbacks.splice(index, 1);
    }
  }

  private executeEndOfZoomCallbacks() {
    var callbackCopy = this.endOfZoomCallbacks.slice();
    for (let cb = 0; cb < callbackCopy.length; cb++) {
      callbackCopy[cb]();
    }
  }

  updateTransformation(
    rotation: number,
    scale: number,
    clientScale: number,
    clientSideRotation: number,
    numberOfFrames: number  // currently never used
  ) {
    if (numberOfFrames === undefined) {
      numberOfFrames = this.MAX_TRANSFORM_FRAMES;
    }

    this.ctx.targetClientSideRotation = clientSideRotation;
    var transformItemData = {
      startRotation: this.ctx.rotation,
      targetRotation: rotation,
      startClientSideRotation: this.ctx.clientSideRotation,
      targetClientSideRotation: clientSideRotation,
      startScale: this.ctx.scale,
      targetScale: scale / 100.0,
      frames: numberOfFrames,
      animationEnd: performance.now() + this.ANIMATION_MAX_DURATION,
      clientScaleFactor: clientScale,
    };

    if (
      (((this.ctx.rotation + this.ctx.clientSideRotation) & 3) != ((rotation + clientSideRotation) & 3)) ||
      (this.ctx.scale != scale / 100.0) || (this.ctx.clientScaleFactor != clientScale) || this.zoomRotateService.isOuterCanvasResized()) {
      var data = transformItemData;
      this.transformRunFinished = false;
      this.logger.debug('NEW transformation, current rotation: ' + this.ctx.rotation + ' current scale: ' + this.ctx.scale + ' target data: ' + JSON.stringify(data))

      this.ctx.targetScale = data.targetScale;
      this.ctx.targetRotation = data.targetRotation;
      if (data.clientScaleFactor !== undefined) {
        this.ctx.clientScaleFactor = data.clientScaleFactor;  // same with clientScale in the param
      }

      // used for sync trans
      this.currentAnimations = transformItemData;
      this.transform(data);
    } else {
      this.logger.debug("No changes in rotation or scale.");
    }
  }

  transform(data: any) {
    // rotation update
    if ((data.targetRotation | 0) == 3 && (data.targetRotation | 0) == 0) {
      data.targetRotation = 4;
    }
    if ((data.clientSideRotation | 0) == 3 && (data.clientSideRotation | 0) == 0) {
      data.targetClientSideRotation = 4;
    }
    this.ctx.rotation = data.targetRotation % 4;
    this.ctx.clientSideRotation = data.targetClientSideRotation % 4;

    // scale update
    if (data.clientScaleFactor !== undefined) {
      this.ctx.clientScaleFactor = data.clientScaleFactor;
    }

    this.logger.trace('Transform frame: ' + (data.frame - 1)
      + ' current Rotation: ' + this.ctx.rotation
      + ' start of Rotation: ' + data.startRotation
      + ' change in Rotation: ' + (data.targetRotation - data.startRotation)
      + ' current Scale: ' + this.ctx.scale
      + ' start of Scale: ' + data.startScale
      + ' change in Scale: ' + (data.targetScale - data.startScale));

    this.ctx.rotation = data.targetRotation % 4;
    this.ctx.clientSideRotation = data.targetClientSideRotation % 4;

    // previous will clear all refresh function in the queue
    // (ignore) recursively perform transform to finish animation
    var contextHandler = RcContext.instance.deviceContextHandler;
    if (contextHandler) {
      // As we finished the transformations, update both, clientRotation and clientTargetRotation to clientTargetRotation
      contextHandler.updateWithClientRotation(data.targetClientSideRotation % 4, data.targetClientSideRotation % 4);
      contextHandler.updateWithClientScale(data.targetScale);
    } else {
      console.error('contextHandler is null, we are unable to update rotation and scale');
    }

    this.transformRunFinished = this.currentAnimations === undefined;
    this.currentAnimations = undefined;
    this.executeEndOfZoomCallbacks();

    // NOTE: If change canvasOuter here, will have scroll bar when zoom in (app-work-panel-rc2)
    /**
     * NOTE: most jump change are caused by the "refresh" operation during the transition.
     * "refresh" will clear the canvasOuter and re-draw the skin and image to the canvas.
     * During the transition, we haven't receive the new resolution image, the canvasOuter is larger
     * than what it should be, in this case, the DeviceCanvas will draw the image in the wrong canvasOuter,
     * resulted in the jump change.
     * Simple solution is to avoid refresh when doing the transition.
     *
     * Problem: Don't know whether this change will cause some issue for click or draw event. Need to test.
     * Also, should keep the transition time as short as possible.
     */
    this.isInTransition = true;
    // always draw image to fit canvas
    this.calculateAutoZoomScale();
    this.ctx.scale = data.targetScale;
    this.zoomRotateService.transform(this.autoZoomScale / 100, this.ctx.scale, (data.targetClientSideRotation - data.startClientSideRotation) == 0);
    this.refreshCanvas(true);
  }

  makeCanvasVisible(): void {
    this.refreshCanvas();

    // mcrc.utils.ui.hideConnectingAnimation();
    // mcrc.appUI.toggleTopBarControls(); Not used
    this.ctx.canvas.element.style.display = "inline";
    (<any>this.ctx).forceAutoZoomUpdate = true;

    this.refreshCanvas();
  }

  refreshCanvas(force: boolean = false): void {
    if (this.isInTransition && !force) {
      return;
    }
    // console.log('refresh canvas');
    var data = {};
    const curCanvasContext = this.ctx.canvas.element.getContext("2d");
    curCanvasContext.clearRect(0, 0, this.ctx.canvas.element.width, this.ctx.canvas.element.height);

    if (this.autoZoomActive) {
      this.ctx.targetX = 0;
      this.ctx.targetY = 0;
      this.canvasOuter.style.translate = '';
    }

    const context = this.deviceContextHandler.currentContext();

    // TODO RC Refactor - 2025.1.0: even for the irregular skin, draw skin first
    if (context.skin.isIrregular) {
      this.ctx.canvasObject.renderImage(data);
      this.ctx.canvasObject.renderSkin();
      setTimeout(() => { DeviceScreenRenderingService.drawCurrentTransformationOnSkinCanvas(this.ctx) });
    } else {
      this.ctx.canvasObject.renderSkin();
      this.ctx.canvasObject.renderImage(data);
    }
    this.ctx.canvasObject.overlay();
    this.zoomRotateService.translate(this.ctx.translateX, this.ctx.translateY);
    this.perf.canvasDrawn();
  }

  autoZoomToCurrentViewSize(noAnimation: boolean = false): void {
    // Don't bother auto zooming if canvas is not visible
    if (!this.isCanvasVisible()) {
      return;
    }

    var deviceScale = this.ctx.saveScale;
    var rotation = this.ctx.rotation;

    this.calculateAutoZoomScale();
    if (this.autoZoomScale === undefined) {
      return;
    }

    var clientScaleFactor = deviceScale / this.autoZoomScale;
    deviceScale = this.autoZoomScale;

    // this line was never executed
    // this.updateScaleAndRotation(rotation, deviceScale, clientScaleFactor, this.ctx.clientScaleFactor, noAnimation ? 0 : undefined);
    this.refreshCanvas();
  }

  // NOTE: Rotation will still use the HTMLCanvasElement to achieve instead of using style
  clientSideRotate(clockwise: boolean): void {
    var clientSideRotation = this.ctx.clientSideRotation;
    if (clockwise) {
      clientSideRotation++;
    } else {
      clientSideRotation--;
    }
    clientSideRotation = (clientSideRotation + 4) & 3;

    this.deviceContextHandler.updateWithClientRotation(undefined, clientSideRotation);

    /**
     * NOTE: When auto-zoom is active, we need to re-calculate the scale before rotation
     * Cannot simply rotate it.
     */
    if (this.isCanvasVisible()) {
      var ret = this.checkIfAutoZoomAndGetScale();
      if (this.shouldRotationAndScaleUpdate(this.ctx.targetRotation, ret.scale, ret.clientScale)) {
        this.updateScaleAndRotation(this.ctx.targetRotation, ret.scale, ret.clientScale, clientSideRotation);
      }
      RcContext.instance.session.sendMessage(new VideoRotationMessage(clientSideRotation));
    }
  }

  /**
   * NOTE: Current pan button is enabled by detecting the comparison between canvasOuter.clientWidth
   * and canvas.size.width. (rc-zoom.components.ts isZoomed())
   * For the refactored code, it will check ctx.scaleFactor
   */
  transferPanZoom(oldRc: RcContext): void {
    var oldTh = oldRc.deviceScreenTransformService;
    if (oldTh === this) {
      return;
    }
    var oldCtx = oldRc.deviceContext as DeviceContext;

    this.ctx.targetX = oldCtx.targetX;
    this.ctx.targetY = oldCtx.targetY;

    this.ctx.scale = oldCtx.scale;
    this.ctx.saveScale = oldCtx.saveScale;
    this.ctx.targetScale = oldCtx.targetScale;

    this.autoZoomActive = oldTh.getAutoZoomActive();
    this.customZoomScale = oldTh.getCustomZoomScale();
    this.targetScale = oldTh.getTHTargetScale();

    // We don't need to run updateScaleAndRotationFirstScreen as we already have the context and the canvas buffer
    // updateScaleAndRotation = updateScaleAndRotationAfterFirstScreen;
  }

  offsetView(x?: number, y?: number): void {
    this.ctx.targetX += x ?? 0;
    this.ctx.targetY += y ?? 0;
    this.refreshCanvas(true);
  }

  shutdown(): void {
    this.shuttingDown = true;

    this.ctx.canvasObject.clear();
  }

  private isCanvasVisible(): boolean {
    return this.canvasOuter && this._canvasOuter.clientWidth !== 0 && this._canvasOuter.clientHeight !== 0;
  }

  private shouldRotationAndScaleUpdate(rotation: number, scale: number, clientScale?: number): boolean {
    var result = true;
    if (this.transformRunFinished &&
      (this.currentTargetRotation == rotation + this.ctx.clientSideRotation &&
        this.currentTargetScale == scale &&
        this.currentClientScale == clientScale)) {
      result = false;
    }
    else {
      this.currentTargetRotation = rotation + this.ctx.clientSideRotation;
      this.currentTargetScale = scale;
      this.currentClientScale = clientScale;
    }
    if ((<any>this.ctx).forceAutoZoomUpdate !== undefined) {
      (<any>this.ctx).forceAutoZoomUpdate = true;
      return true;
    }

    return result;
  }

  /**
   * draw image
   */
  private updateScreen(fullscreen: boolean) {
    var data = {
      fullscreen: fullscreen
    }
    // console.log('update screen')

    // Only reset scale back to 1 when enable the autoZoom
    // var curCanvasContext = this.ctx.canvas.element.getContext("2d");
    // curCanvasContext.clearRect(0, 0, this.ctx.canvas.element.width, this.ctx.canvas.element.height);

    this.isInTransition = false;
    if (this.autoZoomActive) {
      this.zoomRotateService.resetScale();
    }

    const context = this.deviceContextHandler.currentContext();
    // TODO RC Refactor - 2025.1.0: even for the irregular skin, draw skin first
    if (context.skin.isIrregular) {
      this.ctx.canvasObject.renderImage(data);
      this.ctx.canvasObject.renderSkin();
      setTimeout(() => { DeviceScreenRenderingService.drawCurrentTransformationOnSkinCanvas(this.ctx) });
    } else {
      this.ctx.canvasObject.renderSkin();
      this.ctx.canvasObject.renderImage(data);
    }
    this.ctx.canvasObject.overlay();
    this.perf.canvasDrawn();
  }

  private handleImageLoadingEvent = (image: HTMLImageElement, frameData: IFrameData, fullscreen: boolean, imageDataFlags, semaphore): void => {
    this.ctx.imageCapture = image;
    this.ctx.imageCapture.isFullScreen = fullscreen;

    // Swap device width and height when there is no skin applied and the capture and height dimensions do not match
    var rotated = fullscreen &&
      ((this.ctx.skinScreenWidth > this.ctx.skinScreenHeight && this.ctx.imageCapture.width < this.ctx.imageCapture.height) ||
        (this.ctx.skinScreenWidth < this.ctx.skinScreenHeight && this.ctx.imageCapture.width > this.ctx.imageCapture.height));

    // JL: HD-608 - this fix will leave projected screen un-rotated, instead of reverse-rotating it first and then rotating back (see DeviceCanvas.draw.image())
    // ctx.rotateSkinOnly = ctx.deviceSupportsScreenProjection; // rotateSkinOnly never used

    if (rotated) {
      // We do not want to swap dimensions when rotationNonWindows (iOS) since it sends bad image data (rotated incorrectly)
      if (!(<any>this.ctx).rotationNonWindows) {
        // swap with/height and screen offset for projected screen
        var tmp = this.ctx.skinScreenWidth;
        this.ctx.skinScreenWidth = this.ctx.skinScreenHeight;
        this.ctx.skinScreenHeight = tmp;

        tmp = this.ctx.screenOffsetX;
        this.ctx.screenOffsetX = this.ctx.screenOffsetY;
        this.ctx.screenOffsetY = tmp;

        tmp = this.ctx.deviceHeight;
        this.ctx.deviceHeight = this.ctx.deviceWidth;
        this.ctx.deviceWidth = tmp;
      }
    }

    // The jpeg image received directly from agent is upside down
    if ((imageDataFlags & ScreenMessageFlags.IsReversedImage) !== 0) {
      var canvasBuffer = document.createElement("canvas");
      this.ctx.imageCapture = DeviceScreenRenderingService.adjustJpegImage(image, canvasBuffer);
      this.ctx.imageCapture.isFullScreen = fullscreen;
    }

    this.ctx.canvasObject.renderBuffer(frameData, fullscreen);
    if (this.isCanvasVisible() && fullscreen) {
      var ret = this.checkIfAutoZoomAndGetScale();
      if (this.shouldRotationAndScaleUpdate(this.ctx.targetRotation, ret.scale, ret.clientScale)) {
        this.updateScaleAndRotation(this.ctx.rotation, ret.scale, ret.clientScale, ret.clientSideRotation);
      }
    }

    --semaphore.cnt;
    if (semaphore.cnt == 0) {
      this.updateScreen(fullscreen);

      if (semaphore.ack) {
        RcContext.instance.session.sendMessage(new ThAckMessage());
      }
    }
  }

  private processFrameData = (data: IScreenPacket, jfif: Uint8Array, tables: Uint8Array, index: number, semaphore: any) => {
    var type = data.isJpegCodec ? 'image/jpeg' : 'image/png';
    var image = data.frameData[index].binImageData;
    if (index > 0) {
      image = this.addJFIFHeader(image, jfif);
      image = this.addHuffmanTables(image, tables);
    }

    DeviceScreenRenderingService.createImageFromBinaryData(image, type, (image) => this.handleImageLoadingEvent(image, data.frameData[index], data.fullscreen, data.imageDataFlags, semaphore));
  }

  private updateCapture(screenPacket: IScreenPacket) {
    let data = screenPacket;
    // console.log(`frame length: ${data.frameData.length}, ack required: ${data.ackRequired}`)
    /**
     * NOTE: This counter is used to achieve the same functionality as the Semaphore in the oldJS.
     * Each updateCapture will eventually trigger one updateScreen at the end of it.
     */
    let semaphore = {
      cnt: data.frameData.length + 1,
      batchSize: data.frameData.length <= this.FRAME_BATCH_THRESHOLD || this.FRAME_BATCH_FACTOR === 0
        ? data.frameData.length + 2
        : Math.ceil((data.frameData.length + 1) * this.FRAME_BATCH_FACTOR),
      frameLength: data.frameData.length,
      startTime: Date.now(),
      ack: data.ackRequired
    }

    let jfif, tables;
    if ((data.imageDataFlags & ScreenMessageFlags.CompressedJFIF) !== 0 && data.frameData.length > 1) {
      jfif = this.extractJFIFHeader(data.frameData[0].binImageData);
      tables = this.extractHuffmanTables(data.frameData[0].binImageData);
    }

    for (let images = 0; images < data.frameData.length; images++) {
      this.processFrameData(data, jfif, tables, images, semaphore);
    }

    if (--semaphore.cnt == 0) {
      this.updateScreen(data.fullscreen);

      if (semaphore.ack) {
        RcContext.instance.session.sendMessage(new ThAckMessage());
      }
    }
  }

  // private batchLog: string[] = [];
  /** maintains batch parameters based on the speed of the delta screen processing */
  private updateBatchParameters(semaphore) {
    // const today = new Date();
    // setTimeout(() => {
    //   const logInfo = `Batch processed at ${today.getHours()}:${today.getMinutes()}:${today.getSeconds()} ${semaphore.frameLength} frames in ${today.getTime() - semaphore.startTime} ms. [FRAME_BATCH_FACTOR: ${this.FRAME_BATCH_FACTOR}, FRAME_BATCH_THRESHOLD: ${this.FRAME_BATCH_THRESHOLD}]`;
    //   this.batchLog.push(logInfo);
    //   if (this.batchLog.length > 200) {
    //     this.batchLog.forEach((line) => {
    //       console.info(line);
    //     });
    //     this.batchLog = [];
    //   }
    // });

    // do not change parameters for small batches, there is nothing to improve
    // and adjustments for increased speed are irrelevant on small batches
    if (semaphore.frameLength <= this.FRAME_BATCH_MIN_THRESHOLD || this.FRAME_BATCH_FACTOR === 0) {
      return;
    }
    let now = Date.now();
    if (now - semaphore.startTime >= 200) {
      if (semaphore.frameLength > this.FRAME_BATCH_THRESHOLD) {
        this.FRAME_BATCH_FACTOR = Math.max(0.3, this.FRAME_BATCH_FACTOR - 0.01);
      } else {
        this.FRAME_BATCH_THRESHOLD = Math.max(this.FRAME_BATCH_MIN_THRESHOLD, this.FRAME_BATCH_THRESHOLD - 1);
      }
    } else {
      if (semaphore.frameLength >= this.FRAME_BATCH_THRESHOLD) {
        this.FRAME_BATCH_FACTOR = Math.min(1, this.FRAME_BATCH_FACTOR + 0.01);
      } else {
        this.FRAME_BATCH_THRESHOLD = Math.min(this.FRAME_BATCH_MAX_THRESHOLD, this.FRAME_BATCH_THRESHOLD + 1);
      }
    }
  }

  /**
   * Calculates the rotation the device should have, considering all part rotations
   * device current rotation, client side rotation, etc
   */
  private calculateComposedRotation(): number {
    const context = this.deviceContextHandler.currentContext();

    // Client rotation adds on top or device rotation.
    const rotationSum = context.device.rotation + context.client.targetRotation;

    // We just care about rotation values between 0 and 3 included,
    // any other value is just an extra spin
    const composedRotation = rotationSum % 4;

    this.logger.trace('composedRotation: ' + composedRotation + '; device: ' + context.device.rotation + '; client;' + context.client.targetRotation);

    return composedRotation;
  }

  /**
   * Calculates the scale we should render everything at, based on the skin device dimensions,
   * the canvas size, and the composed rotation
   */
  private calculateAutoZoomScale() {

    const context = this.deviceContextHandler.currentContext();

    const composedRotation = this.calculateComposedRotation();

    // First we assume device is being render without rotation (nor client or device side)
    // We use the skin width and height as that is the outermost image we will render
    let widthToRender = context.skin.width;
    let heightToRender = context.skin.height;

    // If the skin is not in the same direction of the device, then switch width and height for the scale calculation
    if (MathHelpers.isEven(context.skin.rotation) && MathHelpers.isOdd(composedRotation)
      || MathHelpers.isOdd(context.skin.rotation) && MathHelpers.isEven(composedRotation)) {
      widthToRender = context.skin.height;
      heightToRender = context.skin.width;
    }

    var widthScale = this.calculateScale(this.canvasOuter.clientWidth, widthToRender);
    var heightScale = this.calculateScale(this.canvasOuter.clientHeight, heightToRender);

    this.autoZoomScale = Math.min(widthScale, heightScale);

    this.logger.trace("autoZoomScale: " + this.autoZoomScale);
  }

  private calculateScale(viewSize: number, deviceSize: number): number {
    return (viewSize / deviceSize) * 100;
  }

  /**  @param scale:  The scale we received from the agent */
  private calculateAutoZoomParameters(scale: number): number {
    this.calculateAutoZoomScale();
    // scale: The scale we have the image at
    // autoZoomScale: The scale that fits the canvas
    // returns how much we should change the scale of the image we have to make it fit the scale of the canvas we should use
    return scale / this.autoZoomScale;
  }

  private calculateCustomZoomParameters(scale: number): number {
    return scale / this.customZoomScale;
  }

  private updateScaleAndRotationFirstScreen = (
    rotation: number,
    scale: number,
    clientScale: number,
    clientSideRotation: number,
    numberOfFrames?: number,   // NOTE: currently all transfer will been finished in one frame
    imageDataFlags?: number
  ) => {
    this.logger.debug('updateScaleAndRotationFirstScreen' + JSON.stringify({ rotation, scale, clientScale, clientSideRotation }));
    if (imageDataFlags !== undefined) {
      this.ctx.scale = scale / 100.0;
      this.ctx.targetScale = scale / 100.0
      this.ctx.rotation = rotation;
      this.ctx.clientScaleFactor = clientScale;

      // Update RcTransformService
      // this.zoomRotateService.init(scale, rotation);

      // Update the context with the initial client scale value
      RcContext.instance.deviceContextHandler.updateWithClientScale(scale / 100.0);

      this.updateScaleAndRotation = this.updateScaleAndRotationAfterFirstScreen;
      if (OsDetectionService.IsAndroid(this.ctx.osType) || OsDetectionService.IsUniversalWindows(this.ctx.osType)) {
        this.ctx.deviceSupportsScreenProjection = (imageDataFlags & ScreenMessageFlags.DeviceProjection) != 0;

        // Do a check for screen projection here as we do not want both enabled at the same time
        if (!this.ctx.deviceSupportsScreenProjection) {
          (<any>this.ctx).rotationNonWindows = (imageDataFlags & ScreenMessageFlags.RotationNonWindows) != 0;
        }
      } else {
        this.ctx.deviceSupportsScreenProjection = true; // WinCE and Windows desktop and iOS
      }

      this.makeCanvasVisible();
      // this.zoomRotateService.init();

      var evt = document.createEvent('HTMLEvents');
      evt.initEvent('rcFirstScreenLoaded', true, true);
      document.dispatchEvent(evt);
    }
  }

  private updateScaleAndRotation = this.updateScaleAndRotationFirstScreen;

  private updateScaleAndRotationAfterFirstScreen = (
    rotation: number,
    scale: number,
    clientScale: number,
    clientSideRotation: number,
    numberOfFrames: number,   // NOTE: currently all transfer will been finished in one frame
    imageDataFlags?: number
  ) => {
    this.logger.debug('updateScaleAndRotationFirstScreen' + JSON.stringify({ rotation, scale, clientScale, clientSideRotation }));
    this.updateTransformation(rotation, scale, clientScale, clientSideRotation, numberOfFrames);
  }

  private extractJFIFHeader(image: Uint8Array): Uint8Array {
    let jfif = new Uint8Array(0);
    for (let i = 2; i < image.length; i++) {    // skip FF D8
      if (image[i] === 0xff) {
        if (image[i + 1] === 0xc0) {        // start of frame
          break;
        }

        // APP and DQT
        const len = (image[i + 2] << 8) + image[i + 3] + 2;
        const pos = jfif.length;
        jfif = ArrayHelper.realloc(jfif, len);
        ArrayHelper.copy(jfif, pos, image, i, len);
        i += len - 1;
      }
    }
    return jfif;
  }


  private addJFIFHeader(image: Uint8Array, jfif?: Uint8Array | null): Uint8Array {
    if (jfif === undefined || jfif === null || jfif.length === 0) {
      return image;
    }

    const len = jfif.length;
    const buf = new Uint8Array(image.length + len);
    buf[0] = image[0];      // FF
    buf[1] = image[1];      // D8

    ArrayHelper.copy(buf, 2, jfif);
    ArrayHelper.copy(buf, 2 + len, image, 2, image.length - 2);

    return buf;
  }

  private extractHuffmanTables(image: Uint8Array): Uint8Array {
    let jfif = new Uint8Array(0);
    for (let i = 2; i < image.length; i++) {    // skip FF D8
      if (image[i] === 0xff) {
        if (image[i + 1] === 0xda) {        // start of scan
          break;
        }

        const len = (image[i + 2] << 8) + image[i + 3] + 2;
        if (image[i + 1] === 0xc4) {        // DHT
          const pos = jfif.length;
          jfif = ArrayHelper.realloc(jfif, len);
          ArrayHelper.copy(jfif, pos, image, i, len);
        }
        i += len - 1;
      }
    }
    return jfif;
  }

  private addHuffmanTables(image: Uint8Array, jfif: Uint8Array): Uint8Array {
    if (jfif === undefined || jfif === null || jfif.length === 0) {
      return image;
    }

    for (let i = 2; i < image.length; i++) {    // skip FF D8
      if (image[i] === 0xff) {
        if (image[i + 1] === 0xda) {        // start of scan
          const len = jfif.length;
          const buf = new Uint8Array(image.length + len);

          ArrayHelper.copy(buf, 0, image, 0, i);
          ArrayHelper.copy(buf, i, jfif, 0, len);
          ArrayHelper.copy(buf, i + len, image, i, image.length - i);

          return buf;
        }

        const len = (image[i + 2] << 8) + image[i + 3] + 2;
        i += len - 1;
      }
    }

    return image;
  }
}
